import { useCallback, useRef, useState } from 'react';

/**
 * Active raid energy-stream VFX (general → raiding unit).
 */
export function useRaidAnimations() {
  const [activeAnimations, setActiveAnimations] = useState(new Map());
  const completedAnimationsRef = useRef(new Set());
  const pendingTimersRef = useRef(new Set());

  const registerRaidAnimation = useCallback((animationData) => {
    if (!animationData?.fromSlot || !animationData?.toSlot) {
      throw new Error('useRaidAnimations.registerRaidAnimation: fromSlot and toSlot are required');
    }

    const animationKey = animationData.animationKey
      || `raid-${animationData.raidUnitInstanceId || 'unknown'}-${Date.now()}`;

    if (completedAnimationsRef.current.has(animationKey)) {
      return;
    }

    setActiveAnimations((prev) => {
      if (prev.has(animationKey)) {
        return prev;
      }
      const next = new Map(prev);
      next.set(animationKey, {
        ...animationData,
        key: animationKey,
        registeredAt: Date.now(),
      });
      return next;
    });
  }, []);

  const scheduleRaidAnimation = useCallback((animationData, delayMs = 0) => {
    const safeDelay = Number.isFinite(delayMs) && delayMs >= 0 ? delayMs : 0;
    const timerId = window.setTimeout(() => {
      pendingTimersRef.current.delete(timerId);
      registerRaidAnimation(animationData);
    }, safeDelay);
    pendingTimersRef.current.add(timerId);
  }, [registerRaidAnimation]);

  const completeRaidAnimation = useCallback((animationKey) => {
    completedAnimationsRef.current.add(animationKey);
    setActiveAnimations((prev) => {
      if (!prev.has(animationKey)) {
        return prev;
      }
      const next = new Map(prev);
      next.delete(animationKey);
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    pendingTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
    pendingTimersRef.current.clear();
    completedAnimationsRef.current.clear();
    setActiveAnimations(new Map());
  }, []);

  return {
    activeAnimations: Array.from(activeAnimations.values()),
    registerRaidAnimation,
    scheduleRaidAnimation,
    completeRaidAnimation,
    reset,
  };
}

export default useRaidAnimations;
